import React, { useState, useCallback, useEffect } from "react";
import AsyncSelect from "react-select/async";

import { customFetch } from '../apiClient';

function toDatetimeLocalValue(date) {
  const d = new Date(date);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function getLocalDateTimeString(datetimeLocalStr) {
  return datetimeLocalStr;
}

const IPDRoomUpdate = () => {
  const [selectedIpd, setSelectedIpd] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [exitMap, setExitMap] = useState({});
  const token = sessionStorage.getItem("token");

  // Auto-dismiss alerts
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // ✅ UPDATED: Load patient options with Reg Date
  const loadIpdOptions = useCallback(async (inputValue) => {
    if (!inputValue) return [];
    try {
     const res = await customFetch(`/Ipds/list?search=${encodeURIComponent(inputValue)}`);
      if (!res.ok) return [];
      const data = await res.json();
      
      return data.map((item) => {
        // Format Date (Fallback to ipD_Date if regDate is not available)
        const regDateFormatted = item.regDate || item.ipD_Date
            ? new Date(item.regDate || item.ipD_Date).toLocaleDateString('en-IN', { 
                day: '2-digit', month: 'short', year: 'numeric' 
              }) 
            : "N/A";

        return {
          label: `${item.name} (${item.mobileNo}) | UHID: ${item.uhiD_No} | IPD No: ${item.ipD_SRNO} | Reg Date: ${regDateFormatted}`,
          value: item.ipD_ID,
        };
      });
    } catch {
      return [];
    }
  }, [token]);

  const fetchRecords = useCallback(async (ipdId) => {
    if (!ipdId) { setRecords([]); return; }
    setLoading(true); setError("");
    try {
     const res = await customFetch(`/IPDRoom/search-by-ipd?rIpdId=${ipdId}`);
      if (!res.ok) throw new Error("Failed to fetch records");
      const data = await res.json();
      setRecords(data);
      const m = {};
      data.forEach(r => {
        if (r.exitDate) m[r.rM_TID] = toDatetimeLocalValue(r.exitDate);
      });
      setExitMap(m);
    } catch (e) {
      setError(e.message || "Failed to fetch records");
      setRecords([]);
      setExitMap({});
    } finally { setLoading(false); }
  }, [token]);

  const handleIpdChange = (opt) => {
    setSelectedIpd(opt || null);
    fetchRecords(opt?.value || null);
  };

  const handleExitChange = (rmTid, v) => {
    setExitMap(prev => ({ ...prev, [rmTid]: v }));
  };

  const updateExitDate = async (record) => {
    const picked = exitMap[record.rM_TID];
    if (!picked) throw new Error("Please pick Exit Date/Time");

    const localDateTimeString = getLocalDateTimeString(picked);

  const res = await customFetch(`/IPDRoom/deallocate/${record.rM_TID}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ exitDate: localDateTimeString }),
    });

    if (!res.ok) {
      const msg = await res.text();
      throw new Error(msg || "Failed to update Exit Date");
    }
  };

  const handleDeallocate = async (row) => {
    if (!window.confirm("Are you sure you want to deallocate this room?")) return;
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      await updateExitDate(row);
      setSuccess("Room deallocated successfully!");
      await fetchRecords(selectedIpd?.value);
    } catch (e) {
      setError(e.message || "Deallocation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (rmTid) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    setLoading(true); 
    setError("");
    setSuccess("");
    try {
     const res = await customFetch(`/IPDRoom/${rmTid}`, {
        method: "DELETE"
      });
      if (!res.ok) throw new Error("Delete failed");
      setSuccess("Room record deleted successfully!");
      setRecords(prev => prev.filter(r => r.rM_TID !== rmTid));
    } catch (e) {
      setError(e.message || "Delete failed");
    } finally { setLoading(false); }
  };

  // ✅ DYNAMIC STYLES FOR REACT SELECT (COMPACT)
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '30px', /* ✅ Compact Height */
      height: '30px',
      fontSize: '11px', /* ✅ Compact Font */
      borderRadius: '6px',
      borderColor: state.isFocused ? 'var(--theme-color, #0088cc)' : '#cbd5e0',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(0, 0, 0, 0.05)' : 'none',
      '&:hover': { borderColor: 'var(--theme-color, #0088cc)' }
    }),
    valueContainer: (base) => ({ ...base, padding: '0 6px' }),
    input: (base) => ({ ...base, margin: '0', padding: '0' }),
    indicatorsContainer: (base) => ({ ...base, height: '30px' }),
    dropdownIndicator: (base) => ({ ...base, padding: '4px' }),
    clearIndicator: (base) => ({ ...base, padding: '4px' }),
    option: (base, state) => ({
      ...base,
      fontSize: '11px',
      padding: '6px 10px',
      backgroundColor: state.isSelected 
        ? 'var(--theme-color, #0088cc)' 
        : state.isFocused 
          ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)' 
          : 'white',
      color: state.isSelected ? 'white' : '#333',
      cursor: 'pointer',
      '&:active': { backgroundColor: 'var(--theme-color, #0088cc)', color: 'white' }
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      fontSize: '11px',
      borderRadius: '6px'
    }),
    placeholder: (base) => ({ ...base, color: '#a0aec0', fontSize: '11px' })
  };

  return (
    <div className="room-update-container compact-ui">
      <div className="room-update-card">
        {/* Header */}
        <div className="room-update-header">
          <div className="header-left">
            <div className="header-icon">
              <i className="bi bi-building"></i>
            </div>
            <div className="header-text">
              <h5>Room Booking Management</h5>
              <p>Search and manage room assignments for IPD patients</p>
            </div>
          </div>
        </div>

        <div className="card-content">
          {/* Search Section */}
          <div className="search-section">
            <label className="search-label">
              <i className="bi bi-search"></i>
              Search Patient
            </label>
            <AsyncSelect
              cacheOptions
              defaultOptions
              loadOptions={loadIpdOptions}
              value={selectedIpd}
              onChange={handleIpdChange}
              isClearable
              placeholder="Search by Name, Mobile No or IPD SRNO..."
              menuPortalTarget={document.body}
              styles={selectStyles} 
            />
          </div>

          {/* Alerts */}
          {error && (
            <div className="alert-custom alert-danger slide-in">
              <i className="bi bi-exclamation-triangle-fill"></i>
              <span>{error}</span>
              <button className="alert-close" onClick={() => setError("")}><i className="bi bi-x"></i></button>
            </div>
          )}
          {success && (
            <div className="alert-custom alert-success slide-in">
              <i className="bi bi-check-circle-fill"></i>
              <span>{success}</span>
              <button className="alert-close" onClick={() => setSuccess("")}><i className="bi bi-x"></i></button>
            </div>
          )}

          {loading && (
            <div className="loading-indicator">
              <span className="spinner-border spinner-border-sm me-2"></span>
              Loading...
            </div>
          )}

          {/* Records Table */}
          {selectedIpd && (
            <div className="table-section">
              <div className="table-header-info">
                <div className="table-title">
                  <i className="bi bi-list-check"></i>
                  <span>Room Booking Records</span>
                  {records.length > 0 && (
                    <span className="badge-count">{records.length}</span>
                  )}
                </div>
              </div>

              <div className="table-responsive">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th>Room Category</th>
                      <th>Room No</th>
                      <th>Entry Date</th>
                      <th>Exit Date</th>
                      <th className="text-center">Room Rate</th>
                      <th>Remarks</th>
                      <th className="text-center" style={{ width: '80px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.length === 0 && !loading ? (
                      <tr>
                        <td colSpan="7" className="empty-state">
                          <i className="bi bi-inbox"></i>
                          <p>No records found</p>
                        </td>
                      </tr>
                    ) : (
                      records.map((r) => {
                        const exitVal = exitMap[r.rM_TID] || "";
                        const isExited = !!r.exitDate;
                        return (
                          <tr key={r.rM_TID}>
                            <td>
                              <span className="badge-category">{r.roomCatge}</span>
                            </td>
                            <td className="fw-medium">{r.roomNo}</td>
                            <td className="text-muted date-text">
                              {r.entryDate ? new Date(r.entryDate).toLocaleString('en-IN', {
                                day: '2-digit', month: '2-digit', year: 'numeric',
                                hour: '2-digit', minute: '2-digit', hour12: true
                              }) : '-'}
                            </td>
                            <td>
                              <input
                                type="datetime-local"
                                className="datetime-input"
                                value={exitVal}
                                onChange={(e) => handleExitChange(r.rM_TID, e.target.value)}
                                disabled={loading || isExited}
                              />
                            </td>
                            <td className="text-center">
                              <span className="fw-semibold text-success">₹{r.roomRate}</span>
                            </td>
                            <td className="text-muted small-text">{r.remarks || "-"}</td>
                            <td className="text-center">
                              <div className="btn-group-actions">
                                <button
                                  className="btn-icon btn-deallocate"
                                  disabled={isExited || loading || !exitVal}
                                  title="Set Exit Date and Deallocate"
                                  onClick={() => handleDeallocate(r)}
                                  type="button"
                                >
                                  <i className="bi bi-box-arrow-in-left"></i>
                                </button>
                                <button
                                  className="btn-icon btn-delete"
                                  disabled={loading}
                                  title="Delete"
                                  onClick={() => handleDelete(r.rM_TID)}
                                  type="button"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ✅ COMPACT UI STYLES ✅ */}
      <style jsx>{`
        .compact-ui {
          font-family: system-ui, -apple-system, sans-serif;
        }

        .room-update-container { padding: 0; }

        .room-update-card {
          background: white; border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
          border: 1px solid #e0e6ed; overflow: visible;
        }

        /* Animations */
        @keyframes slideIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        .slide-in { animation: slideIn 0.2s ease; }

        /* Header */
        .room-update-header {
          background: var(--theme-color, #0088cc);
          padding: 10px 16px; border-bottom: 2px solid rgba(0,0,0,0.1);
          border-top-left-radius: 8px; border-top-right-radius: 8px;
        }

        .header-left { display: flex; align-items: center; gap: 10px; }

        .header-icon {
          width: 32px; height: 32px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 6px; display: flex; align-items: center; justify-content: center;
          font-size: 16px; color: white;
        }

        .header-text h5 { margin: 0; color: white; font-size: 15px; font-weight: 600; line-height: 1.1; }
        .header-text p { margin: 0; color: rgba(255, 255, 255, 0.85); font-size: 10px; }

        .card-content { padding: 12px 16px; }

        .search-section { margin-bottom: 12px; position: relative; z-index: 40; }

        .search-label {
          display: flex; align-items: center; gap: 4px; font-size: 11px;
          font-weight: 600; color: #2c3e50; margin-bottom: 4px;
        }
        .search-label i { color: var(--theme-color, #0088cc); font-size: 10px; }

        /* Alerts */
        .alert-custom {
          margin-bottom: 12px; padding: 8px 12px; border-radius: 6px;
          display: flex; align-items: center; gap: 8px;
          font-size: 11px; font-weight: 500; position: relative;
        }
        .alert-danger { background: #fee; color: #c33; border: 1px solid #fcc; }
        .alert-success { background: #d4edda; color: #155724; border: 1px solid #c3e6cb; }
        .alert-custom i { font-size: 12px; }
        .alert-close { position: absolute; right: 8px; background: transparent; border: none; cursor: pointer; opacity: 0.6; padding: 0; }
        .alert-close:hover { opacity: 1; }

        .loading-indicator {
          padding: 12px; text-align: center; color: var(--theme-color, #0088cc);
          font-size: 11px; font-weight: 500;
        }

        .table-section {
          background: white; border-radius: 8px; padding: 0; border: 1px solid #e0e6ed; overflow: hidden;
        }

        .table-header-info {
          display: flex; justify-content: space-between; align-items: center;
          padding: 10px 12px; border-bottom: 1px solid #e0e6ed; background: #f8f9fa;
        }

        .table-title { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; color: #2c3e50; }
        .table-title i { color: var(--theme-color, #0088cc); font-size: 14px; }

        .badge-count {
          background: var(--theme-color, #0088cc);
          color: white; padding: 2px 6px; border-radius: 10px; font-size: 10px;
        }

        .table-responsive { overflow-x: auto; }
        .custom-table { width: 100%; border-collapse: collapse; font-size: 11px; }

        .custom-table thead {
          background: var(--theme-color, #0088cc);
        }

        .custom-table thead th {
          color: white; font-weight: 600; padding: 6px 8px;
          font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; border: none;
        }

        .custom-table tbody tr { transition: all 0.2s; border-bottom: 1px solid #e0e6ed; }
        .custom-table tbody tr:hover { background: #f8f9fa; }
        .custom-table tbody td { padding: 6px 8px; vertical-align: middle; }

        .text-center { text-align: center; }
        .fw-medium { font-weight: 500; }
        .fw-semibold { font-weight: 600; }
        .text-success { color: #27ae60; }
        .text-muted { color: #7f8c8d; }
        .small-text { font-size: 10px; }
        .date-text { font-size: 10px; }

        .badge-category {
          display: inline-block; padding: 2px 6px; border-radius: 4px;
          background: color-mix(in srgb, var(--theme-color, #0088cc), white 90%);
          color: var(--theme-color, #0088cc);
          font-size: 10px; font-weight: 600; text-transform: uppercase;
        }

        /* Form Controls Inline */
        .datetime-input {
          width: 100%; padding: 4px 6px; border: 1px solid #cbd5e0; border-radius: 4px;
          font-size: 11px; height: 26px; transition: all 0.2s; outline: none; background: white;
        }
        .datetime-input:focus { border-color: var(--theme-color, #0088cc); }
        .datetime-input:disabled { background: #f3f4f6; opacity: 0.6; cursor: not-allowed; }

        .empty-state { text-align: center; padding: 30px 20px; color: #7f8c8d; }
        .empty-state i { font-size: 32px; margin-bottom: 8px; display: block; opacity: 0.5; }
        .empty-state p { margin: 0; font-size: 11px; }

        /* Action Buttons */
        .btn-group-actions { display: flex; justify-content: center; gap: 4px; }

        .btn-icon {
          width: 24px; height: 24px; padding: 0; display: inline-flex; align-items: center; justify-content: center;
          border-radius: 4px; border: none; cursor: pointer; transition: all 0.2s; font-size: 11px;
        }

        .btn-deallocate { background: #fff3cd; color: #856404; }
        .btn-deallocate:hover:not(:disabled) { background: #ffc107; color: white; transform: translateY(-1px); }
        
        .btn-delete { background: #fee2e2; color: #dc2626; }
        .btn-delete:hover:not(:disabled) { background: #e74c3c; color: white; transform: translateY(-1px); }

        .btn-icon:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

        @media (max-width: 768px) {
          .table-responsive { -webkit-overflow-scrolling: touch; }
          .custom-table { min-width: 650px; }
        }
      `}</style>
    </div>
  );
};

export default IPDRoomUpdate;